import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ILoginDetail, loginStrategyType } from "./types";
import { loginAsync, getFetchConfigurationAsync } from "./loginThunks";

export interface LoginState {
  accessKey: string;
  secretKey: string;
  sts: string;
  useSTS: boolean;
  jwt: string;
  mfaToken: string;
  otp: string;

  error: string;
  loginSending: boolean;
  loggedIn: boolean;
  loadingFetchConfiguration: boolean;
  status: "idle" | "loading" | "failed";

  loginStrategy: ILoginDetail[];
  redirectRules: [];
  isK8S: boolean;
  navigateTo: string;
  navigateFrom: string;
}

const initialState: LoginState = {
  accessKey: "",
  secretKey: "",
  sts: "",
  useSTS: false,
  jwt: "",
  mfaToken: "",
  otp: "",

  error: "",
  loginSending: false,
  loggedIn: false,
  loadingFetchConfiguration: true,
  status: "idle",

  loginStrategy: [
    {
      loginStrategy: loginStrategyType.unknown,
      redirectRules: [],
    },
  ],
  redirectRules: [],
  isK8S: false,
  navigateTo: "",
  navigateFrom: "",
};

export const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setAccessKey: (state, action: PayloadAction<string>) => {
      state.accessKey = action.payload;
    },
    setSecretKey: (state, action: PayloadAction<string>) => {
      state.secretKey = action.payload;
    },
    setUseSTS: (state, action: PayloadAction<boolean>) => {
      state.useSTS = action.payload;
    },
    setSTS: (state, action: PayloadAction<string>) => {
      state.sts = action.payload;
    },
    setJwt: (state, action: PayloadAction<string>) => {
      state.jwt = action.payload;
    },
    setOTP: (state, action: PayloadAction<string>) => {
      state.otp = action.payload;
    },
    setNavigateTo: (state, action: PayloadAction<string>) => {
      state.navigateTo = action.payload;
    },
    setNavigateFrom: (state, action: PayloadAction<string>) => {
      state.navigateFrom = action.payload;
    },
    resetForm: (state) => initialState,
    setLoggedIn: (state, action: PayloadAction<boolean>) => {
      state.loggedIn = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFetchConfigurationAsync.pending, (state, action) => {
        state.loadingFetchConfiguration = true;
      })
      .addCase(getFetchConfigurationAsync.rejected, (state, action) => {
        state.loadingFetchConfiguration = false;
      })
      .addCase(getFetchConfigurationAsync.fulfilled, (state, action) => {
        state.loadingFetchConfiguration = false;
        if (action.payload) {
          state.loginStrategy = action.payload;
        }
      })
      .addCase(loginAsync.pending, (state) => {
        state.status = "loading";
        state.loginSending = true;
        state.loggedIn = false;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.loginSending = false;
        state.loggedIn = true;
      })
      .addCase(loginAsync.rejected, (state) => {
        state.loginSending = false;
        state.loggedIn = false;
        state.status = "failed";
        state.error = "Invalid login";
      });
  },
});

export const {
  setAccessKey,
  setSecretKey,
  setUseSTS,
  setSTS,
  setJwt,
  setOTP,
  setNavigateTo,
  setNavigateFrom,
  resetForm,
  setLoggedIn,
} = loginSlice.actions;

export default loginSlice.reducer;
